import type { ActivityEvent, Collaborator, Project } from "@todoer/shared";

const verbs: Record<ActivityEvent["event"], string> = {
  added: "added", updated: "updated", completed: "completed", uncompleted: "reopened", deleted: "deleted",
  restored: "restored", moved: "moved", commented: "commented on", shared: "shared", unshared: "removed someone from",
  viewed: "viewed",
};

/** "Sam completed "Write copy" in #Launch" */
export function describeActivity(e: ActivityEvent, people: Collaborator[], projects: Project[], meId?: string): { who: string; text: string; where: string } {
  if (e.objectType === "agenda") {
    // An agent reading the user's agenda: "Hermes read your agenda for Tuesday 4 November".
    const whose = e.userId === meId ? "your" : `${people.find((p) => p.id === e.userId)?.name ?? "someone"}'s`;
    return { who: e.extra?.via ?? "An app", text: `read ${whose} agenda for ${e.extra?.content ?? e.objectId}`, where: "" };
  }
  const who = e.userId === meId ? "You" : people.find((p) => p.id === e.userId)?.name ?? "Someone";
  const p = projects.find((x) => x.id === e.projectId);
  const where = p ? (p.isInbox ? "Inbox" : `#${p.name}`) : e.extra?.projectName ? `#${e.extra.projectName}` : "";
  const thing = e.extra?.content ? `"${e.extra.content}"` : e.objectType;
  let text = `${verbs[e.event]} ${e.objectType === "project" ? `project ${thing}` : thing}`;
  if (e.event === "shared" && e.extra?.assigneeId) {
    const target = e.extra.assigneeId === meId ? "you" : people.find((x) => x.id === e.extra!.assigneeId)?.name ?? "someone";
    text = `shared project ${thing} with ${target}`;
  }
  if (e.event === "added" && e.extra?.assigneeId && e.extra.assigneeId !== e.userId) {
    const target = e.extra.assigneeId === meId ? "you" : people.find((x) => x.id === e.extra!.assigneeId)?.name ?? "someone";
    text += ` and assigned it to ${target}`;
  }
  if (e.extra?.via) text += ` via ${e.extra.via}`;
  return { who, text, where };
}

/** Where an activity row leads: the task panel for task and comment events, the project page for project events. */
export function activityTarget(e: Pick<ActivityEvent, "objectType" | "objectId" | "projectId">): { kind: "task"; id: string } | { kind: "project"; id: string } | null {
  if (e.objectType === "task" || e.objectType === "comment") return { kind: "task", id: e.objectId };
  if (e.objectType === "project") return { kind: "project", id: e.objectId };
  if (e.projectId) return { kind: "project", id: e.projectId };
  return null;
}
